"use client";

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  User,
  Calendar,
  FileText,
  MapPin,
  Clock,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  Pickaxe,
  Gem,
  Mountain,
} from "lucide-react";
import axios from "axios";

export default function UserDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const userId = params.id;

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userReports, setUserReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiError, setApiError] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [imageError, setImageError] = useState(null);
  const reportsPerPage = 3;
  const BASE_URL = 'https://backend.emerald.azadnoori.com';

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setApiError(null);

      try {
        // Fetch user
        const userResponse = await axios.get(`${BASE_URL}/api/v1/users/${userId}`, {
          withCredentials: true,
        });
        const fetchedUser = userResponse.data.data.user;
        setUser(fetchedUser);

        // Fetch reports for this user
        const reportsResponse = await axios.get(
          `${BASE_URL}/api/v1/reports/userreports/${userId}`,
          { withCredentials: true }
        );

        const reports = reportsResponse.data.data.reports.map((report) => {
          let images = report.images || [];
          if (typeof report.images === "string" && report.images.trim() !== "") {
            try {
              images = JSON.parse(report.images);
              if (!Array.isArray(images)) {
                images = [];
              }
            } catch (err) {
              images = [];
            }
          }
          return {
            ...report,
            images,
          };
        });
        setUserReports(reports);

        // Set first report as selected
        if (reports.length > 0) {
          setSelectedReport(reports[0]);
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to load user or reports.";
        setApiError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // Fetch images for selected report
  useEffect(() => {
    const fetchImages = async () => {
      if (!selectedReport?.images || !Array.isArray(selectedReport.images) || selectedReport.images.length === 0) {
        setImageUrls([]);
        setImageError("No images associated with this report.");
        return;
      }

      setImageError(null);

      const urls = await Promise.all(
        selectedReport.images.map(async (photo, index) => {
          try {
            const url = photo.startsWith("http") ? photo : `${BASE_URL}${photo}?t=${Date.now()}`;
            const response = await fetch(url, {
              method: "GET",
              headers: {
                Accept: "image/*",
              },
            });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            return blobUrl;
          } catch (error) {
            setImageError(`Failed to load image ${index + 1}: ${error.message}`);
            return "/placeholder.svg?height=192&width=192&text=Image+Failed";
          }
        })
      );
      setImageUrls(urls);
      return () => urls.forEach(URL.revokeObjectURL);
    };

    fetchImages();
  }, [selectedReport]);

  // Listen for reportAdded to refresh reports
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "reportAdded") {
        const fetchReports = async () => {
          try {
            const response = await axios.get(
              `${BASE_URL}/api/v1/reports/userreports/${userId}`,
              { withCredentials: true }
            );
            const reports = response.data.data.reports.map((report) => {
              let images = report.images || [];
              if (typeof report.images === "string" && report.images.trim() !== "") {
                try {
                  images = JSON.parse(report.images);
                  if (!Array.isArray(images)) {
                    images = [];
                  }
                } catch (err) {
                  images = [];
                }
              }
              return {
                ...report,
                images,
              };
            });
            setUserReports(reports);
            if (reports.length > 0) {
              setSelectedReport(reports[0]);
            }
          } catch (error) {
            setApiError(error.response?.data?.message || "Failed to refresh reports.");
          }
        };
        fetchReports();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [userId]);

  // Handle report deletion
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/v1/reports/${reportId}`, {
        withCredentials: true,
      });

      // Update the reports state by removing the deleted report
      const updatedReports = userReports.filter((report) => report.id !== reportId);
      setUserReports(updatedReports);

      // If the deleted report was the selected one, reset or select another
      if (selectedReport?.id === reportId) {
        setSelectedReport(updatedReports[0] || null);
      }

      // Update pagination if needed
      if (updatedReports.length <= indexOfFirstReport && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

      toast.success("Report successfully deleted!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      if (error.response?.status === 404) {
        // Report not found: Remove from UI and refresh reports
        const updatedReports = userReports.filter((report) => report.id !== reportId);
        setUserReports(updatedReports);
        if (selectedReport?.id === reportId) {
          setSelectedReport(updatedReports[0] || null);
        }
        if (updatedReports.length <= indexOfFirstReport && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
        toast.info("Report was already deleted or does not exist.", {
          position: "top-right",
          autoClose: 3000,
        });

        // Refresh reports to ensure UI is in sync with database
        try {
          const response = await axios.get(
            `${BASE_URL}/api/v1/reports/userreports/${userId}`,
            { withCredentials: true }
          );
          const reports = response.data.data.reports.map((report) => {
            let images = report.images || [];
            if (typeof report.images === "string" && report.images.trim() !== "") {
              try {
                images = JSON.parse(report.images);
                if (!Array.isArray(images)) {
                  images = [];
                }
              } catch (err) {
                images = [];
              }
            }
            return {
              ...report,
              images,
            };
          });
          setUserReports(reports);
          if (reports.length > 0 && !updatedReports.find((r) => r.id === selectedReport?.id)) {
            setSelectedReport(reports[0]);
          }
        } catch (refreshError) {
          setApiError("Failed to refresh reports after deletion.");
        }
      } else {
        // Handle other errors (e.g., 403, 500)
        const errorMessage =
          error.response?.data?.message || "Failed to delete report.";
        setApiError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  // Pagination
  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = userReports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages = Math.ceil(userReports.length / reportsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate total mining metrics
  const totalCaving = userReports.reduce((total, report) => total + (report.caving || 0), 0);
  const totalUselessStones = userReports.reduce(
    (total, report) => total + (report.useless_stones || 0),
    0
  );
  const totalEmeralds = userReports.reduce(
    (total, report) => total + (report.emeralds_found || 0),
    0
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1, ease: "linear" }}
            className="mx-auto mb-4"
          >
            <Loader2 size={40} className="text-emerald-600" />
          </motion.div>
          <h2 className="text-xl font-medium text-gray-700">Loading miner data...</h2>
        </div>
      </div>
    );
  }

  if (!user || apiError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-6 bg-white rounded-xl shadow-lg">
          <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-700 mb-2">
            {apiError || "Miner not found"}
          </h2>
          <p className="text-gray-500 mb-4">
            {apiError
              ? apiError
              : "The miner you're looking for doesn't exist or has been removed."}
          </p>
          <button
            onClick={() => navigate("/dashboard/admin")}
            className="mt-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center mx-auto"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate("/dashboard/admin")}
          className="mb-6 text-emerald-600 hover:underline flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" /> Back to dashboard
        </button>

        {/* User Profile Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-emerald-600 h-32 relative"></div>
          <div className="px-6 pb-6 relative">
            <div className="absolute -top-16 left-6 bg-white rounded-full p-2 shadow-lg">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                {user.profile_image ? (
                  <img
                    src={`${BASE_URL}${user.profile_image}`}
                    alt={`${user.firstname} ${user.lastname}`}
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <span
                  className="text-gray-600 font-medium text-2xl"
                  style={{ display: user.profile_image ? "none" : "flex" }}
                >
                  {user.firstname?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
            </div>
            <div className="pt-20">
              <h1 className="text-3xl font-bold text-gray-800">
                {user.firstname} {user.lastname}
              </h1>
              <p className="text-gray-500">{user.email}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="bg-emerald-100 text-emerald-800 text-sm font-medium px-3 py-1 rounded-full">
                  {user.role}
                </span>
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                  {user.plan || "No Plan"}
                </span>
                <span className="text-gray-500 text-sm flex items-center">
                  <FileText size={16} className="mr-1" /> {user.reports} reports
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mining Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Caving</h3>
            <div className="flex items-center">
              <Mountain size={24} className="text-emerald-600 mr-3" />
              <span className="text-3xl font-bold text-gray-800">{totalCaving}</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Useless Stones</h3>
            <div className="flex items-center">
              <Pickaxe size={24} className="text-emerald-600 mr-3" />
              <span className="text-3xl font-bold text-gray-800">{totalUselessStones}</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Emeralds Found</h3>
            <div className="flex items-center">
              <Gem size={24} className="text-emerald-600 mr-3" />
              <span className="text-3xl font-bold text-emerald-600">{totalEmeralds}</span>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-6">Mining Reports</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reports List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-medium text-gray-700">Reports List</h3>
                <div className="text-sm text-gray-500">
                  {indexOfFirstReport + 1}-{Math.min(indexOfLastReport, userReports.length)} of{" "}
                  {userReports.length}
                </div>
              </div>

              <div className="divide-y max-h-[500px] overflow-y-auto">
                {currentReports.length > 0 ? (
                  currentReports.map((report) => (
                    <motion.div
                      key={report.id}
                      whileHover={{ backgroundColor: "rgba(243, 244, 246, 0.5)" }}
                      className={`p-4 cursor-pointer ${
                        selectedReport?.id === report.id ? "bg-emerald-50" : ""
                      }`}
                      onClick={() => setSelectedReport(report)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center text-sm text-gray-500 mb-1">
                            <Calendar size={14} className="mr-1" />{" "}
                            {new Date(report.date).toLocaleDateString()}
                          </div>
                          <div className="flex items-center text-sm font-medium">
                            <MapPin size={14} className="mr-1" /> {report.location || "N/A"}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-right">
                            <div className="text-sm font-medium text-emerald-600">
                              {report.emeralds_found} emeralds
                            </div>
                            <div className="text-xs text-gray-500">{report.caving} caving</div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent selecting the report when clicking delete
                              handleDeleteReport(report.id);
                            }}
                            className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                            title="Delete report"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">No mining reports found</div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t flex justify-between items-center">
                  <button
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`w-8 h-8 rounded-full ${
                          currentPage === number
                            ? "bg-emerald-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Report Details */}
          <div className="lg:col-span-2">
            {selectedReport ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                key={selectedReport.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-6 border-b">
                  <h3 className="text-xl font-bold text-gray-800">Mining Report</h3>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <p className="text-gray-500 text-sm flex items-center">
                      <Calendar size={14} className="mr-1" />{" "}
                      {new Date(selectedReport.date).toLocaleDateString()}
                    </p>
                    <p className="text-gray-500 text-sm flex items-center">
                      <MapPin size={14} className="mr-1" /> {selectedReport.location || "N/A"}
                    </p>
                    <p className="text-gray-500 text-sm flex items-center">
                      <Clock size={14} className="mr-1" /> {selectedReport.duration || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Caving Amount</p>
                      <p className="text-2xl font-bold text-gray-800">{selectedReport.caving}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Useless Stones</p>
                      <p className="text-2xl font-bold text-gray-800">
                        {selectedReport.useless_stones}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Emeralds Found</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {selectedReport.emeralds_found}
                      </p>
                    </div>
                  </div>

                  {selectedReport.notes && (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-700 mb-2">Notes</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">{selectedReport.notes}</p>
                      </div>
                    </div>
                  )}

                  <h4 className="font-medium text-gray-700 mb-4">Site Images</h4>
                  {imageError && <p className="text-red-500 mb-4">{imageError}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {imageUrls.length > 0 ? (
                      imageUrls.map((url, index) => (
                        <div key={index} className="rounded-lg overflow-hidden border">
                          <img
                            src={url}
                            alt={`Mining site image ${index + 1}`}
                            className="w-full h-48 object-contain bg-gray-100"
                            onError={(e) => {
                              e.target.src = "/placeholder.svg?height=192&width=192&text=Image+Failed";
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">No images available.</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <p className="text-gray-500">Select a report to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}